import { useState, useEffect, useRef, useCallback } from 'react';
import { fetchActiveOrders, updateOrderStatus, saveNotification, type Order } from '@/lib/supabase';
import { unlockAudio, useAudioUnlockStatus } from '@/hooks/useOrderNotification';

// ── Simple ring sound using Web Audio API ─────────────────────────────────────
function playRingOnce() {
  try {
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.3);
    gain.gain.setValueAtTime(0.6, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.5);
  } catch { /* ignore */ }
}

const REJECT_REASONS = [
  'Item out of stock',
  'Delivery not available in this area',
  'Customer unreachable',
  'Suspicious order',
  'Order duplicate',
];

interface ChatMessage {
  id: string;
  type: 'bot' | 'user' | 'order' | 'action';
  text?: string;
  order?: Order;
  time: number;
}

interface OrderChatBotProps {
  /** If provided, only show for this specific order. Otherwise polls Supabase. */
  externalOrder?: Order | null;
  onAccept?: (order: Order) => void;
  onReject?: (order: Order, reason: string) => void;
  /** Admin mode - shows permission gate first */
  requirePermission?: boolean;
}

// Permission storage keys
const PERMISSION_KEY = 'orderbot_permission_granted';
const BACKGROUND_MODE_KEY = 'orderbot_background_mode';

export default function OrderChatBot({ externalOrder, onAccept, onReject, requirePermission = true }: OrderChatBotProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [pendingOrders, setPendingOrders] = useState<Order[]>([]);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [showRejectFor, setShowRejectFor] = useState<string | null>(null);
  const [customReason, setCustomReason] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [pulse, setPulse] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [permissionGranted, setPermissionGranted] = useState(() => {
    if (!requirePermission) return true;
    return localStorage.getItem(PERMISSION_KEY) === 'true';
  });
  const [backgroundMode, setBackgroundMode] = useState(() => {
    return localStorage.getItem(BACKGROUND_MODE_KEY) === 'true';
  });
  const [showPermissionGate, setShowPermissionGate] = useState(false);
  const [backgroundEnabled, setBackgroundEnabled] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const knownIdsRef = useRef<Set<string>>(new Set());
  const ringIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioUnlocked = useAudioUnlockStatus();
  const swRegistrationRef = useRef<ServiceWorkerRegistration | null>(null);

  // Check permission on mount
  useEffect(() => {
    if (requirePermission && !permissionGranted) {
      setShowPermissionGate(true);
    }
  }, [requirePermission, permissionGranted]);

  // Register service worker and setup background sync
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    
    navigator.serviceWorker.ready.then(registration => {
      swRegistrationRef.current = registration;
      
      // Check if periodic background sync is supported
      if ('periodicSync' in registration) {
        // @ts-expect-error - periodicSync is not in standard types yet
        registration.periodicSync.getTags().then((tags: string[]) => {
          setBackgroundEnabled(tags.includes('order-sync'));
        }).catch(() => {});
      }
    });
  }, []);

  const enableBackgroundMode = useCallback(async () => {
    if (!swRegistrationRef.current) return false;
    
    try {
      // Request background sync permission
      // @ts-expect-error - periodicSync is not in standard types yet
      const status = await navigator.permissions.query({
        name: 'periodic-background-sync',
      });
      
      if (status.state === 'granted') {
        // @ts-expect-error - periodicSync is not in standard types yet
        await swRegistrationRef.current.periodicSync.register('order-sync', {
          minInterval: 30000, // 30 seconds minimum
        });
        localStorage.setItem(BACKGROUND_MODE_KEY, 'true');
        setBackgroundMode(true);
        setBackgroundEnabled(true);
        return true;
      }
    } catch (err) {
      console.log('Background sync not supported, using fallback');
    }
    
    // Fallback: just save preference, SW will handle polling when active
    localStorage.setItem(BACKGROUND_MODE_KEY, 'true');
    setBackgroundMode(true);
    return true;
  }, []);

  const grantPermission = useCallback(async () => {
    await unlockAudio();
    localStorage.setItem(PERMISSION_KEY, 'true');
    setPermissionGranted(true);
    
    // Enable background mode automatically
    await enableBackgroundMode();
    
    setShowPermissionGate(false);
    setIsOpen(true);
    setIsMinimized(false);
  }, [unlockAudio, enableBackgroundMode]);

  const addMessage = useCallback((msg: Omit<ChatMessage, 'id' | 'time'>) => {
    const newMsg: ChatMessage = { ...msg, id: Date.now().toString() + Math.random(), time: Date.now() };
    setMessages(prev => [...prev, newMsg]);
  }, []);

  const startRinging = useCallback(() => {
    if (ringIntervalRef.current) return;
    if (audioUnlocked) playRingOnce();
    setPulse(true);
    ringIntervalRef.current = setInterval(() => {
      if (audioUnlocked) playRingOnce();
      setPulse(p => !p);
    }, 2500);
  }, [audioUnlocked]);

  const stopRinging = useCallback(() => {
    if (ringIntervalRef.current) {
      clearInterval(ringIntervalRef.current);
      ringIntervalRef.current = null;
    }
    setPulse(false);
  }, []);

  // Handle external orders (from admin panel parent)
  useEffect(() => {
    if (!externalOrder) return;
    if (knownIdsRef.current.has(externalOrder.id)) return;
    knownIdsRef.current.add(externalOrder.id);

    setPendingOrders(prev => [...prev, externalOrder]);
    addMessage({
      type: 'bot',
      text: `Naya order aaya! 🛍️ ${externalOrder.customer_name} ne order kiya hai.`,
    });
    addMessage({ type: 'order', order: externalOrder });

    setIsOpen(true);
    setIsMinimized(false);
    setUnreadCount(p => p + 1);
    startRinging();
  }, [externalOrder, addMessage, startRinging]);

  // Send pending orders to service worker for background monitoring
  const syncWithServiceWorker = useCallback((orders: Order[]) => {
    if (!swRegistrationRef.current?.active) return;
    
    const pendingOrders = orders.filter(o => o.status === 'pending');
    if (pendingOrders.length > 0) {
      swRegistrationRef.current.active.postMessage({
        type: 'PENDING_ORDERS_ON_LOAD',
        orders: pendingOrders,
      });
    }
  }, []);

  // Standalone mode: poll Supabase for new orders
  useEffect(() => {
    if (externalOrder !== undefined) return; // controlled externally

    const poll = async () => {
      try {
        const orders = await fetchActiveOrders();
        
        // Always sync with service worker for background monitoring
        syncWithServiceWorker(orders);
        
        const newPending = orders.filter(
          o => o.status === 'pending' && !knownIdsRef.current.has(o.id)
        );

        if (newPending.length > 0) {
          newPending.forEach(o => knownIdsRef.current.add(o.id));
          setPendingOrders(prev => {
            const existingIds = new Set(prev.map(p => p.id));
            return [...prev, ...newPending.filter(o => !existingIds.has(o.id))];
          });

          newPending.forEach(order => {
            addMessage({
              type: 'bot',
              text: `Naya order aaya! 🛍️ ${order.customer_name} ne order kiya hai ₹${order.final_amount} ka.`,
            });
            addMessage({ type: 'order', order });
          });

          setIsOpen(true);
          setIsMinimized(false);
          setUnreadCount(p => p + newPending.length);
          startRinging();
          
          // Also notify service worker about new orders
          if (swRegistrationRef.current?.active) {
            newPending.forEach(order => {
              swRegistrationRef.current!.active!.postMessage({
                type: 'NEW_ORDER',
                order,
              });
            });
          }
        }
      } catch { /* ignore */ }
    };

    // Initial load: seed known IDs without alerting
    fetchActiveOrders().then(orders => {
      orders.forEach(o => knownIdsRef.current.add(o.id));
      syncWithServiceWorker(orders);
    }).catch(() => {});

    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [externalOrder, addMessage, startRinging, syncWithServiceWorker]);

  // Stop ringing when no pending orders
  useEffect(() => {
    if (pendingOrders.length === 0) stopRinging();
  }, [pendingOrders.length, stopRinging]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [messages, isOpen, isMinimized]);

  // Reset unread when opened
  useEffect(() => {
    if (isOpen && !isMinimized) setUnreadCount(0);
  }, [isOpen, isMinimized]);

  const handleAccept = useCallback(async (order: Order) => {
    if (processingId) return;
    setProcessingId(order.id);
    stopRinging();

    // Notify service worker to stop alarm
    if (swRegistrationRef.current?.active) {
      swRegistrationRef.current.active.postMessage({
        type: 'ORDER_ACTION_TAKEN',
        orderId: order.id,
      });
    }

    try {
      if (onAccept) {
        onAccept(order);
      } else {
        await updateOrderStatus(order.id, 'confirmed');
        await saveNotification(order, 'order_confirmed');
      }

      setPendingOrders(prev => prev.filter(o => o.id !== order.id));
      addMessage({
        type: 'user',
        text: `✅ Order Accept kiya! ${order.customer_name} ka order confirm ho gaya.`,
      });
      addMessage({
        type: 'bot',
        text: `Customer ko notification bhej di gai hai. Order #${order.id.slice(-6).toUpperCase()} confirmed!`,
      });
    } finally {
      setProcessingId(null);
    }
  }, [processingId, stopRinging, onAccept, addMessage]);

  const handleReject = useCallback(async (order: Order, reason: string) => {
    if (processingId || !reason) return;
    setProcessingId(order.id);
    stopRinging();

    // Notify service worker to stop alarm
    if (swRegistrationRef.current?.active) {
      swRegistrationRef.current.active.postMessage({
        type: 'ORDER_ACTION_TAKEN',
        orderId: order.id,
      });
    }

    try {
      if (onReject) {
        onReject(order, reason);
      } else {
        await updateOrderStatus(order.id, 'cancelled');
        await saveNotification(order, 'order_cancelled', reason);
      }

      setPendingOrders(prev => prev.filter(o => o.id !== order.id));
      setShowRejectFor(null);
      setRejectReason('');
      setCustomReason('');
      addMessage({
        type: 'user',
        text: `❌ Order Reject kiya. Reason: "${reason}"`,
      });
      addMessage({
        type: 'bot',
        text: `Order cancel ho gaya. Customer ko cancel notification bhej di gai hai.`,
      });
    } finally {
      setProcessingId(null);
    }
  }, [processingId, stopRinging, onReject, addMessage]);

  // Intro message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addMessage({
        type: 'bot',
        text: 'Namaste! Main ClothyBazar Order Bot hoon. Naya order aane pe yahan popup aaunga. 👋',
      });
    }
  }, [isOpen, messages.length, addMessage]);

  const formatTime = (ts: number) => {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Permission Gate Modal */}
      {showPermissionGate && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-sm p-6 shadow-2xl text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
              <i className="ri-robot-2-line text-white text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Order Bot Access</h3>
            <p className="text-gray-500 text-sm mb-4 leading-relaxed">
              Real-time order notifications ke liye permission chahiye. Yeh aapke phone screen pe permanently rahega jab tak orders pending ho.
            </p>
            
            {/* Background Mode Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-4 text-left">
              <p className="text-blue-800 text-xs font-semibold mb-1 flex items-center gap-1">
                <i className="ri-information-line text-blue-500"></i>
                Background Mode
              </p>
              <p className="text-blue-600 text-xs leading-relaxed">
                Agar admin panel band bhi ho, tab bhi order aane pe notification aayega. Phone lock hone ke baad bhi alarm bajega!
              </p>
            </div>
            
            <div className="space-y-3">
              <button
                onClick={grantPermission}
                className="w-full py-4 rounded-2xl bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold text-sm flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-notification-3-fill text-lg"></i>
                Allow Notifications
              </button>
              <button
                onClick={() => setShowPermissionGate(false)}
                className="w-full py-3 rounded-2xl border border-gray-200 text-gray-600 font-semibold text-sm cursor-pointer whitespace-nowrap"
              >
                Not Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Chat Button - Only show after permission */}
      {permissionGranted && (
        <button
          onClick={async () => {
            if (!audioUnlocked) await unlockAudio();
            setIsOpen(true);
            setIsMinimized(false);
          }}
          className={`fixed bottom-6 left-4 z-[55] flex items-center gap-2.5 px-4 py-3 rounded-2xl text-white text-sm font-bold shadow-2xl transition-all cursor-pointer whitespace-nowrap ${
            pendingOrders.length > 0
              ? `bg-gradient-to-r from-orange-500 to-red-500 ${pulse ? 'scale-110 shadow-orange-500/50' : 'scale-100'}`
              : 'bg-gradient-to-r from-gray-800 to-gray-900'
          }`}
          style={{ transition: 'transform 0.3s ease, box-shadow 0.3s ease' }}
        >
          <div className="relative w-5 h-5 flex items-center justify-center">
            <i className={`ri-robot-2-line text-lg ${pendingOrders.length > 0 ? 'animate-bounce' : ''}`}></i>
          </div>
          <span className="hidden sm:inline">Order Bot</span>
          {(unreadCount > 0 || pendingOrders.length > 0) && (
            <span className="flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-white text-orange-600 text-[11px] font-black">
              {pendingOrders.length || unreadCount}
            </span>
          )}
          {pendingOrders.length > 0 && (
            <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-400 animate-ping"></span>
          )}
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className={`fixed z-[56] left-4 right-4 sm:left-auto sm:right-4 sm:w-[360px] flex flex-col bg-white rounded-3xl overflow-hidden shadow-2xl border border-gray-100 transition-all duration-300 ${
          isMinimized ? 'bottom-20 h-16' : 'bottom-20 max-h-[75vh] h-[560px] sm:h-[580px]'
        }`}>

          {/* Header */}
          <div className={`flex items-center justify-between px-4 py-3 shrink-0 ${
            pendingOrders.length > 0
              ? 'bg-gradient-to-r from-orange-500 to-red-500'
              : 'bg-gradient-to-r from-gray-800 to-gray-900'
          }`}>
            <div className="flex items-center gap-3">
              <div className="relative w-9 h-9 flex items-center justify-center">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${pendingOrders.length > 0 ? 'bg-white/20' : 'bg-white/10'}`}>
                  <i className="ri-robot-2-line text-white text-lg"></i>
                </div>
                {pendingOrders.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-green-400 border-2 border-orange-500 animate-pulse"></span>
                )}
              </div>
              <div>
                <p className="text-white font-bold text-sm leading-none">Order Bot</p>
                <p className="text-white/70 text-[11px] mt-0.5">
                  {pendingOrders.length > 0
                    ? `${pendingOrders.length} order${pendingOrders.length > 1 ? 's' : ''} waiting!`
                    : 'Naye orders ka wait kar raha hoon...'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {pendingOrders.length > 0 && (
                <button
                  onClick={stopRinging}
                  className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-all cursor-pointer"
                  title="Ring band karo"
                >
                  <i className="ri-volume-mute-line text-sm"></i>
                </button>
              )}
              <button
                onClick={() => setIsMinimized(p => !p)}
                className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-all cursor-pointer"
              >
                <i className={`${isMinimized ? 'ri-arrow-up-s-line' : 'ri-subtract-line'} text-sm`}></i>
              </button>
              <button
                onClick={() => { setIsOpen(false); stopRinging(); }}
                className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition-all cursor-pointer"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          </div>

          {/* Chat Body */}
          {!isMinimized && (
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-gray-50">
              {messages.map(msg => (
                <div key={msg.id}>
                  {/* Bot message */}
                  {msg.type === 'bot' && (
                    <div className="flex items-start gap-2">
                      <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-robot-2-line text-white text-xs"></i>
                      </div>
                      <div className="max-w-[80%]">
                        <div className="bg-white rounded-2xl rounded-tl-sm px-3 py-2 shadow-sm border border-gray-100">
                          <p className="text-gray-800 text-xs leading-relaxed">{msg.text}</p>
                        </div>
                        <p className="text-gray-400 text-[10px] mt-0.5 ml-1">{formatTime(msg.time)}</p>
                      </div>
                    </div>
                  )}

                  {/* User/admin message */}
                  {msg.type === 'user' && (
                    <div className="flex items-start gap-2 justify-end">
                      <div className="max-w-[80%]">
                        <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl rounded-tr-sm px-3 py-2">
                          <p className="text-white text-xs leading-relaxed">{msg.text}</p>
                        </div>
                        <p className="text-gray-400 text-[10px] mt-0.5 text-right mr-1">{formatTime(msg.time)}</p>
                      </div>
                      <div className="w-7 h-7 rounded-xl bg-gray-200 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-user-line text-gray-600 text-xs"></i>
                      </div>
                    </div>
                  )}

                  {/* Order card */}
                  {msg.type === 'order' && msg.order && (() => {
                    const order = msg.order;
                    const isPending = pendingOrders.some(o => o.id === order.id);
                    const isProcessing = processingId === order.id;
                    const showingReject = showRejectFor === order.id;

                    return (
                      <div className="mx-1">
                        <div className={`rounded-2xl overflow-hidden border-2 ${isPending ? 'border-orange-400 bg-white' : 'border-green-400 bg-green-50'}`}>
                          {/* Order Header */}
                          <div className={`px-3 py-2 flex items-center justify-between ${isPending ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'bg-gradient-to-r from-green-500 to-emerald-500'}`}>
                            <div className="flex items-center gap-2">
                              <i className="ri-shopping-bag-2-fill text-white text-sm"></i>
                              <span className="text-white font-bold text-xs">#{order.id.slice(-6).toUpperCase()}</span>
                            </div>
                            <span className="text-white font-black text-sm">₹{order.final_amount}</span>
                          </div>

                          {/* Customer Info */}
                          <div className="px-3 py-2 border-b border-gray-100">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-7 h-7 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
                                <i className="ri-user-3-line text-orange-600 text-xs"></i>
                              </div>
                              <div>
                                <p className="text-gray-900 font-bold text-xs">{order.customer_name}</p>
                                <p className="text-gray-500 text-[10px] flex items-center gap-1">
                                  <i className="ri-phone-line"></i>{order.customer_phone}
                                </p>
                              </div>
                            </div>
                            <p className="text-gray-400 text-[10px] flex items-center gap-1 mt-0.5">
                              <i className="ri-map-pin-line"></i>{order.customer_city}
                            </p>
                          </div>

                          {/* Items */}
                          <div className="px-3 py-2 border-b border-gray-100">
                            <p className="text-gray-400 text-[10px] font-semibold uppercase tracking-wider mb-1.5">
                              Items ({order.items.reduce((s, i) => s + i.quantity, 0)} pcs)
                            </p>
                            <div className="space-y-1.5">
                              {order.items.map((item, idx) => {
                                let img = item.image;
                                if (!img) {
                                  try {
                                    const listed = JSON.parse(localStorage.getItem('listed_products') ?? '[]');
                                    const match = listed.find((p: { id: number; name?: string; price?: number; thumbnail?: string; images?: string[] }) =>
                                      p.id === item.id || (p.name === item.name && p.price === item.price)
                                    );
                                    if (match) img = match.thumbnail || match.images?.[0] || '';
                                  } catch { /* ignore */ }
                                }
                                return (
                                  <div key={idx} className="flex items-center gap-2 bg-gray-50 rounded-xl p-1.5">
                                    {img ? (
                                      <img src={img} alt={item.name} className="w-9 h-10 rounded-lg object-cover object-top shrink-0" />
                                    ) : (
                                      <div className="w-9 h-10 rounded-lg bg-gray-200 flex items-center justify-center shrink-0">
                                        <i className="ri-t-shirt-line text-gray-400 text-sm"></i>
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <p className="text-gray-800 text-[11px] font-semibold leading-tight truncate">{item.name}</p>
                                      <div className="flex items-center gap-1 flex-wrap mt-0.5">
                                        {item.size && <span className="text-[9px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded font-semibold">S:{item.size}</span>}
                                        {item.selectedColor?.name && (
                                          <span className="flex items-center gap-0.5 text-[9px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded font-semibold">
                                            <span className="w-2 h-2 rounded-full inline-block border border-gray-300" style={{ backgroundColor: item.selectedColor.hex || '#ccc' }}></span>
                                            {item.selectedColor.name}
                                          </span>
                                        )}
                                        <span className="text-[9px] text-gray-500">×{item.quantity}</span>
                                      </div>
                                    </div>
                                    <span className="text-gray-800 text-[11px] font-bold shrink-0">₹{item.price * item.quantity}</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Action Buttons or Status */}
                          {isPending ? (
                            <div className="px-3 py-2">
                              {!showingReject ? (
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleAccept(order)}
                                    disabled={isProcessing}
                                    className="flex-1 py-2.5 rounded-xl bg-green-500 text-white font-bold text-xs flex items-center justify-center gap-1.5 hover:bg-green-600 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap"
                                  >
                                    {isProcessing ? (
                                      <i className="ri-loader-4-line animate-spin"></i>
                                    ) : (
                                      <><i className="ri-check-line"></i>Accept</>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => { setShowRejectFor(order.id); setRejectReason(''); setCustomReason(''); }}
                                    disabled={isProcessing}
                                    className="flex-1 py-2.5 rounded-xl bg-red-50 text-red-600 border border-red-200 font-bold text-xs flex items-center justify-center gap-1.5 hover:bg-red-100 transition-all cursor-pointer disabled:opacity-60 whitespace-nowrap"
                                  >
                                    <i className="ri-close-line"></i>Reject
                                  </button>
                                </div>
                              ) : (
                                <div className="space-y-2">
                                  <p className="text-gray-600 text-[11px] font-semibold flex items-center gap-1">
                                    <i className="ri-error-warning-line text-red-500"></i>
                                    Rejection reason:
                                  </p>
                                  <div className="space-y-1.5 max-h-32 overflow-y-auto">
                                    {REJECT_REASONS.map(r => (
                                      <button
                                        key={r}
                                        onClick={() => setRejectReason(r)}
                                        className={`w-full text-left px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-all cursor-pointer ${
                                          rejectReason === r
                                            ? 'bg-red-50 border-red-400 text-red-700'
                                            : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300'
                                        }`}
                                      >
                                        <i className={`mr-1.5 ${rejectReason === r ? 'ri-radio-button-fill text-red-500' : 'ri-radio-button-line text-gray-400'}`}></i>
                                        {r}
                                      </button>
                                    ))}
                                    <button
                                      onClick={() => setRejectReason('custom')}
                                      className={`w-full text-left px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-all cursor-pointer ${
                                        rejectReason === 'custom'
                                          ? 'bg-red-50 border-red-400 text-red-700'
                                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-gray-300'
                                      }`}
                                    >
                                      <i className={`mr-1.5 ${rejectReason === 'custom' ? 'ri-radio-button-fill text-red-500' : 'ri-radio-button-line text-gray-400'}`}></i>
                                      Other reason...
                                    </button>
                                    {rejectReason === 'custom' && (
                                      <input
                                        type="text"
                                        value={customReason}
                                        onChange={e => setCustomReason(e.target.value)}
                                        placeholder="Apna reason likho..."
                                        maxLength={100}
                                        className="w-full px-2.5 py-1.5 rounded-lg border border-gray-200 text-[11px] outline-none focus:border-red-400"
                                      />
                                    )}
                                  </div>
                                  <div className="flex gap-2 pt-1">
                                    <button
                                      onClick={() => { setShowRejectFor(null); setRejectReason(''); setCustomReason(''); }}
                                      className="flex-1 py-2 rounded-xl border border-gray-200 text-gray-600 text-[11px] font-semibold hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
                                    >
                                      Back
                                    </button>
                                    <button
                                      onClick={() => {
                                        const final = rejectReason === 'custom' ? customReason.trim() : rejectReason;
                                        if (!final) return;
                                        handleReject(order, final);
                                      }}
                                      disabled={isProcessing || !(rejectReason === 'custom' ? customReason.trim() : rejectReason)}
                                      className="flex-1 py-2 rounded-xl bg-red-500 text-white text-[11px] font-bold hover:bg-red-600 transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap"
                                    >
                                      {isProcessing ? <i className="ri-loader-4-line animate-spin"></i> : 'Confirm Reject'}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="px-3 py-2">
                              <div className="flex items-center gap-1.5 text-green-700 text-xs font-semibold">
                                <i className="ri-checkbox-circle-fill text-green-500"></i>
                                Order Processed
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
              <div ref={chatEndRef}></div>
            </div>
          )}

          {/* Minimized View */}
          {isMinimized && pendingOrders.length > 0 && (
            <div className="flex-1 flex items-center px-4 gap-3 bg-orange-50">
              <span className="text-orange-600 animate-pulse text-sm">
                <i className="ri-notification-3-fill"></i>
              </span>
              <p className="text-orange-700 font-bold text-sm">
                {pendingOrders.length} order{pendingOrders.length > 1 ? 's' : ''} waiting for action!
              </p>
              <button
                onClick={() => setIsMinimized(false)}
                className="ml-auto text-orange-600 text-xs font-bold underline cursor-pointer whitespace-nowrap"
              >
                View
              </button>
            </div>
          )}
          
          {/* Background Mode Toggle */}
          {!isMinimized && (
            <div className="px-3 py-2 bg-gray-100 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className={`${backgroundMode ? 'ri-wifi-line text-green-500' : 'ri-wifi-off-line text-gray-400'} text-sm`}></i>
                  <span className="text-gray-600 text-xs font-medium">
                    {backgroundMode ? 'Background Mode ON' : 'Background Mode OFF'}
                  </span>
                </div>
                <button
                  onClick={async () => {
                    if (!backgroundMode) {
                      const enabled = await enableBackgroundMode();
                      if (enabled) {
                        addMessage({
                          type: 'bot',
                          text: '✅ Background mode enabled! Ab app band hone ke baad bhi notifications aayenge.',
                        });
                      }
                    } else {
                      localStorage.setItem(BACKGROUND_MODE_KEY, 'false');
                      setBackgroundMode(false);
                      addMessage({
                        type: 'bot',
                        text: 'Background mode disabled. Notifications sirf app open hone pe aayenge.',
                      });
                    }
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${
                    backgroundMode 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                >
                  {backgroundMode ? 'ON' : 'OFF'}
                </button>
              </div>
              <p className="text-gray-400 text-[10px] mt-1">
                {backgroundMode 
                  ? 'App band hone ke baad bhi order notifications aayenge' 
                  : 'Background mode on karo — app band hone pe bhi notifications milega'}
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
}
